'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { FormattedMessage } from 'react-intl';
import Link from 'next/link';

type SkipLinkProps = {
  linkText: string;
  linkUrl: string[];
  modalId?: string;
  style?: React.CSSProperties;
};

/**
 * Skip link component used for Accessibility.
 * @param {SkipLinkProps} props - Props.
 */
const SkipLink = (props: SkipLinkProps) => {
  const { linkText, linkUrl, modalId = '', style = '' } = props; // linkUrl will be the id of the element to skip to
  const [isVisible, setIsVisible] = useState(false);

  const firstTabPressedRef = useRef(false);
  const skipLinkRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const handleScroll = useCallback((target: Element) => {
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }, []);

  const findElementByTestId = (
    element: HTMLElement,
    testId: string,
  ): HTMLElement | null => {
    if (element.getAttribute('data-testid') === testId) {
      return element;
    }
    for (let i = 0; i < element.children.length; i++) {
      const foundElement = findElementByTestId(
        element.children[i] as HTMLElement,
        testId,
      );
      if (foundElement) {
        return foundElement;
      }
    }
    return null;
  };

  const handleSkip = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        (e as React.KeyboardEvent).key !== 'Enter' &&
        (e as React.KeyboardEvent).key !== ' '
      ) {
        return;
      }

      e.preventDefault();

      const targetElement =
        findElementByTestId(document.body, linkUrl[0]) ||
        findElementByTestId(document.body, linkUrl[1]) ||
        findElementByTestId(document.body, linkUrl[2]);

      if (targetElement) {
        const originalTabIndex = targetElement.getAttribute('tabIndex');
        if (!targetElement.hasAttribute('tabIndex')) {
          targetElement.setAttribute('tabIndex', '-1');
        }
        targetElement.focus();
        handleScroll(targetElement);

        // Restore the original tabIndex after a short delay
        setTimeout(() => {
          if (originalTabIndex === null) {
            targetElement.removeAttribute('tabIndex');
          } else {
            targetElement.setAttribute('tabIndex', originalTabIndex);
          }
        }, 0);
      }
    },
    [handleScroll, linkUrl],
  );

  // this is to force close the skiplink when it shares the page with a modal/overlay
  useEffect(() => {
    const checkForModal = () => {
      const modalElement = document.querySelector(`[data-testid="${modalId}"]`);
      if (!modalElement) {
        setIsVisible(false);
      }
    };

    checkForModal();
    observerRef.current = new MutationObserver(checkForModal);
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [modalId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const isBackwardTab = e.shiftKey; // Detect backward tabbing
        const activeElement = document.activeElement as HTMLElement;

        // Check if the active element is a form input or other focusable element
        // Initially, also included SELECT and BUTTON elements
        const isFormElement =
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA';
        if (!firstTabPressedRef.current && !isFormElement && !isBackwardTab) {
          e.preventDefault(); // Prevent default tabbing behavior
          setIsVisible(true);
          skipLinkRef.current?.focus();
          firstTabPressedRef.current = true;
        } else {
          setIsVisible(false);
        }
      }
    };

    const handleFocus = (e: FocusEvent) => {
      const isSkipLinkFocused = skipLinkRef.current === e.target;
      if (firstTabPressedRef.current && !isSkipLinkFocused) {
        setIsVisible(false);
      }
      if (firstTabPressedRef.current && isSkipLinkFocused) {
        setIsVisible(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('focusin', handleFocus);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('focusin', handleFocus);
    };
  }, []);

  return (
    <Flex
      className={`skip-link ml-2 absolute top-12 left-24 bg-bg-2 p-2 border border-black rounded-md z-[99999] ${
        isVisible
          ? 'pointer-events-auto skip-link--visible top-[50px] left-[100px]'
          : ' pointer-events-none top-[-1000px] left-[-1000px]'
      }`}
      style={{
        opacity: isVisible ? 1 : 0, // Hide visually when not visible
        ...style,
      }}
      role="button"
      ref={skipLinkRef}
      onKeyDown={handleSkip}
      tabIndex={0}
      data-testid="skip-link"
    >
      <Link
        href={`#${linkUrl}`}
        data-testid="accessibility-skip-link"
        tabIndex={-1}
      >
        <P className="text-primary font-bold underline cursor-pointer">
          <FormattedMessage
            id="sidenav.skip.link"
            defaultMessage="Skip to {linkText}"
            values={{ linkText }}
          />
        </P>
      </Link>
    </Flex>
  );
};

export default SkipLink;
